import React, { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { Target, NavigationArrow, Crosshair, CaretRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { getCurrentPosition } from "@/lib/geo";
import { fetchElevations, elevationKey, playsLikeYards } from "@/lib/elevation";
import { fetchWind, shotBearing, headwindComponent, windAdjustedYards, describeWind } from "@/lib/wind";
import { fetchAirTemperatureF, airDensityAdjustedYards } from "@/lib/airDensity";

/**
 * Inline rangefinder for the live scorecard.
 *
 * A focused subset of the full CourseFlyover rangefinder:
 *   - Esri satellite + tee/green markers + distance rings
 *   - Draggable target crosshair → big "yards to pin" readout
 *   - Front / Centre / Back yardages (when the hole has mapped green-front/back)
 *   - "Use my GPS" → blue player dot + measures from real position
 *   - Numbered shot pins for the shots already logged on this hole
 *   - "Score this hole →" CTA — parent component swaps to the scorecard pane
 *   - Optional "Track shot" hook (Pro only) — calls back into the parent
 *   - Plays-like distance overlay (elevation + wind + air density)
 *   - Smart Club Pick (Pro): suggests a club from the user's bag with a
 *     probability of reaching the front of the green, using the plays-like
 *     distance and per-club stdev from /stats/clubs.
 *
 * The full /courses/:id/flyover page is still available via the "Pro tools"
 * button for shot tracing, multi-target measuring, and the bigger map view.
 */

function haversineYards(la1, lo1, la2, lo2) {
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(la2 - la1);
  const dLng = toRad(lo2 - lo1);
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(la1)) * Math.cos(toRad(la2)) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(a)) * 1.0936133);
}

/** Rational-approximation of the Gauss error function (Abramowitz & Stegun
 *  7.1.26), accurate to ~1.5e-7. Used by the Smart Club Pick to translate
 *  the player's club-distance stdev into a "% chance of reaching" number
 *  via the normal CDF: P(X ≥ target) = 0.5·(1 + erf(z/√2)) where
 *  z = (avg − target) / stdev. Implemented inline so we don't pull in a
 *  whole stats library for a single function call. */
function erf(x) {
  const sign = Math.sign(x);
  const absX = Math.abs(x);
  const a1 =  0.254829592, a2 = -0.284496736, a3 = 1.421413741;
  const a4 = -1.453152027, a5 =  1.061405429, p = 0.3275911;
  const t = 1 / (1 + p * absX);
  const y = 1 - (((((a5 * t + a4) * t) + a3) * t + a2) * t + a1) * t * Math.exp(-absX * absX);
  return sign * y;
}

export default function RangefinderInline({
  hole, holeNumber, totalYards, par, shots = [], canTrack = false,
  onTrackShot, onScore, onOpenFullFlyover, goalAtRisk = false,
}) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const targetMarkerRef = useRef(null);
  const playerMarkerRef = useRef(null);
  const lineRef = useRef(null);
  const [target, setTarget] = useState(null);
  const [playerPos, setPlayerPos] = useState(null);
  const [locatingPlayer, setLocatingPlayer] = useState(false);
  // Plays-like environmental correction. Fetched once per hole change from
  // Open-Meteo (free, no key). All three values are nullable until the
  // network calls resolve — the chip renders the moment we have wind, then
  // upgrades to a full plays-like number once elevation + temperature land.
  const [wind, setWind] = useState(null);
  const [tempF, setTempF] = useState(null);
  const [elevations, setElevations] = useState({}); // {key → metres}
  // Smart Club Pick: cached user bag (Pro-only). Each entry has avg_yards
  // and (when ≥ 5 shots logged) stdev_yards — we use both to recommend a
  // specific club + a probability that it'll reach the front of the green.
  const [bag, setBag] = useState(null);

  const teePos = hole?.tee_lat != null ? [hole.tee_lat, hole.tee_lng] : null;
  const greenPos = hole?.green_lat != null ? [hole.green_lat, hole.green_lng] : null;
  const greenFront = hole?.green_front_lat != null ? [hole.green_front_lat, hole.green_front_lng] : null;
  const greenBack = hole?.green_back_lat != null ? [hole.green_back_lat, hole.green_back_lng] : null;

  // Seed target to mid-fairway whenever the hole switches.
  useEffect(() => {
    if (!teePos || !greenPos) { setTarget(null); return; }
    setTarget({
      lat: (teePos[0] + greenPos[0]) / 2,
      lng: (teePos[1] + greenPos[1]) / 2,
    });
    setPlayerPos(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holeNumber, hole?.tee_lat, hole?.green_lat]);

  // Fetch the hole's environmental data once whenever the hole changes. We
  // sample one point per call (green centre for wind + temperature, plus
  // origin/target/green for elevation) and rely on the lib-level caches to
  // de-dupe identical points across re-renders.
  useEffect(() => {
    if (!greenPos) return;
    let cancelled = false;
    fetchWind(greenPos[0], greenPos[1]).then((w) => { if (!cancelled) setWind(w); });
    fetchAirTemperatureF(greenPos[0], greenPos[1]).then((t) => { if (!cancelled) setTempF(t); });
    const points = [];
    if (teePos) points.push({ lat: teePos[0], lng: teePos[1] });
    points.push({ lat: greenPos[0], lng: greenPos[1] });
    fetchElevations(points).then((map) => {
      if (cancelled) return;
      setElevations((prev) => {
        const out = { ...prev };
        map.forEach((v, k) => { out[k] = v; });
        return out;
      });
    });
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holeNumber, hole?.tee_lat, hole?.green_lat]);

  // Build the map once per hole change.
  useEffect(() => {
    if (!mapEl.current || (!greenPos && !teePos && shots.length === 0)) return;
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }

    const map = L.map(mapEl.current, {
      zoomControl: true, attributionControl: false, scrollWheelZoom: false,
      doubleClickZoom: true, dragging: true, tap: true, maxZoom: 19,
    });
    mapRef.current = map;

    // Tap on map (outside markers) repositions the target so the player can
    // jump it to any spot they intend to aim at without needing to drag.
    map.on("click", (e) => setTarget({ lat: e.latlng.lat, lng: e.latlng.lng }));

    L.tileLayer("https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}", {
      maxZoom: 19, maxNativeZoom: 17, crossOrigin: true,
    }).addTo(map);
    L.tileLayer("https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}.png", {
      maxZoom: 19, opacity: 0.85, subdomains: ["a", "b", "c", "d"], crossOrigin: true,
    }).addTo(map);

    if (teePos && greenPos) {
      L.polyline([teePos, greenPos], { color: "#FFC72C", weight: 3, opacity: 0.9, dashArray: "6 6" }).addTo(map);
    }
    if (greenPos) {
      [100, 150, 200].forEach((y) => {
        L.circle(greenPos, {
          radius: y * 0.9144, color: "white", weight: 1, opacity: 0.55,
          fillOpacity: 0, dashArray: "3 6",
        }).addTo(map);
      });
    }
    if (teePos) {
      const teeIcon = L.divIcon({
        className: "",
        html: `<div style="width:22px;height:22px;border-radius:9999px;background:#0F2537;color:white;display:flex;align-items:center;justify-content:center;font-weight:900;font-size:11px;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,.45)">T</div>`,
        iconSize: [22, 22], iconAnchor: [11, 11],
      });
      L.marker(teePos, { icon: teeIcon }).addTo(map);
    }
    if (greenPos) {
      const pinIcon = L.divIcon({
        className: "",
        html: `<div style="width:28px;height:28px;border-radius:9999px;background:#1E5631;color:white;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:14px;border:2px solid white;box-shadow:0 4px 12px rgba(15,37,55,.45)">⛳</div>`,
        iconSize: [28, 28], iconAnchor: [14, 14],
      });
      L.marker(greenPos, { icon: pinIcon }).addTo(map)
        .bindTooltip(`${totalYards}y`, { direction: "top", offset: [0, -14] });
    }
    // Render the existing shots on the hole.
    shots.forEach((s, i) => {
      const shotIcon = L.divIcon({
        className: "",
        html: `<div style="width:24px;height:24px;border-radius:9999px;background:#E05D3A;color:white;display:flex;align-items:center;justify-content:center;font-weight:800;font-size:11px;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,.35)">${i + 1}</div>`,
        iconSize: [24, 24], iconAnchor: [12, 12],
      });
      L.marker([s.lat, s.lng], { icon: shotIcon }).addTo(map).bindTooltip(s.club || "Shot", { direction: "top" });
    });
    // Fit
    const pts = [];
    if (teePos) pts.push(teePos);
    if (greenPos) pts.push(greenPos);
    shots.forEach((s) => pts.push([s.lat, s.lng]));
    if (pts.length >= 2) map.fitBounds(pts, { padding: [28, 28], maxZoom: 17 });
    else if (pts.length === 1) map.setView(pts[0], 16);

    targetMarkerRef.current = null;
    playerMarkerRef.current = null;
    lineRef.current = null;

    return () => { if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; } };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holeNumber, hole?.tee_lat, hole?.green_lat, shots.length, totalYards]);

  // Target + player marker effect.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (target) {
      const icon = L.divIcon({
        className: "",
        html: `<div style="width:40px;height:40px;border-radius:9999px;border:3px solid white;background:rgba(224,93,58,.18);display:flex;align-items:center;justify-content:center;box-shadow:0 4px 14px rgba(15,37,55,.5)"><div style="width:8px;height:8px;border-radius:9999px;background:white"></div></div>`,
        iconSize: [40, 40], iconAnchor: [20, 20],
      });
      if (!targetMarkerRef.current) {
        const m = L.marker([target.lat, target.lng], { icon, draggable: true, autoPan: true, zIndexOffset: 1000 }).addTo(map);
        m.on("drag", (e) => {
          const ll = e.target.getLatLng();
          setTarget({ lat: ll.lat, lng: ll.lng });
        });
        targetMarkerRef.current = m;
      } else {
        targetMarkerRef.current.setLatLng([target.lat, target.lng]);
      }
    } else if (targetMarkerRef.current) {
      map.removeLayer(targetMarkerRef.current);
      targetMarkerRef.current = null;
    }
    if (playerPos) {
      const icon = L.divIcon({
        className: "",
        html: `<div style="width:18px;height:18px;border-radius:9999px;background:#3B82F6;border:3px solid white;box-shadow:0 0 0 6px rgba(59,130,246,.25)"></div>`,
        iconSize: [18, 18], iconAnchor: [9, 9],
      });
      if (!playerMarkerRef.current) {
        playerMarkerRef.current = L.marker([playerPos.lat, playerPos.lng], { icon, zIndexOffset: 900 }).addTo(map);
      } else {
        playerMarkerRef.current.setLatLng([playerPos.lat, playerPos.lng]);
      }
    } else if (playerMarkerRef.current) {
      map.removeLayer(playerMarkerRef.current);
      playerMarkerRef.current = null;
    }
    // White origin → target → green line.
    if (lineRef.current) { map.removeLayer(lineRef.current); lineRef.current = null; }
    const origin = playerPos ? [playerPos.lat, playerPos.lng] : teePos;
    const ll = [];
    if (origin) ll.push(origin);
    if (target) ll.push([target.lat, target.lng]);
    if (greenPos) ll.push(greenPos);
    if (target && ll.length >= 2) {
      lineRef.current = L.polyline(ll, { color: "white", weight: 3, opacity: 0.95 }).addTo(map);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, playerPos]);

  // Live yardages.
  const range = useMemo(() => {
    if (!greenPos || !target) return null;
    const origin = playerPos ? [playerPos.lat, playerPos.lng] : teePos;
    return {
      toCentre: haversineYards(target.lat, target.lng, greenPos[0], greenPos[1]),
      toFront: greenFront ? haversineYards(target.lat, target.lng, greenFront[0], greenFront[1]) : null,
      toBack: greenBack ? haversineYards(target.lat, target.lng, greenBack[0], greenBack[1]) : null,
      carry: origin ? haversineYards(origin[0], origin[1], target.lat, target.lng) : null,
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, playerPos, greenPos?.[0], teePos?.[0]]);

  // Plays-like: layer elevation (1 yd per foot of rise) + wind (+1.5 %/mph
  // headwind, –1 %/mph tail) + air density (temperature + altitude) on top
  // of the flat distance. Returns null until the required data lands.
  const playsLike = useMemo(() => {
    if (!range || !target || !greenPos) return null;
    const origin = playerPos ? [playerPos.lat, playerPos.lng] : teePos;
    if (!origin) return null;
    const oKey = elevationKey(origin[0], origin[1]);
    const gKey = elevationKey(greenPos[0], greenPos[1]);
    const oElev = elevations[oKey];
    const gElev = elevations[gKey];
    // Elevation step (only when we have both elevations).
    let centre_elev = range.toCentre;
    if (oElev != null && gElev != null) {
      centre_elev = playsLikeYards(range.toCentre, gElev - oElev);
    }
    // Wind step.
    let centre_wind = centre_elev;
    let headwind_mph = 0;
    let bearing = null;
    if (wind) {
      bearing = shotBearing(origin[0], origin[1], greenPos[0], greenPos[1]);
      headwind_mph = headwindComponent(wind, bearing);
      centre_wind = windAdjustedYards(centre_elev, headwind_mph);
    }
    // Air density step (uses green altitude — Fiji ~ 4m, Denver ~ 1609m).
    const centre_air = airDensityAdjustedYards(centre_wind, tempF, gElev);
    return {
      toCentre: centre_air,
      delta_m: gElev != null && oElev != null ? Math.round((gElev - oElev) * 10) / 10 : null,
      headwind_mph: Math.round(headwind_mph * 10) / 10,
      wind_label: wind && bearing != null ? describeWind(wind, bearing) : null,
    };
  }, [range, target, playerPos, teePos, greenPos, elevations, wind, tempF]);

  // Fetch the user's club bag once on mount. Endpoint returns 402 for
  // non-Pro users — we swallow that and leave bag = null so the Smart
  // Club Pick UI just doesn't render.
  useEffect(() => {
    if (!canTrack) return; // canTrack mirrors user.is_pro
    let cancelled = false;
    api.get("/stats/clubs")
      .then((r) => { if (!cancelled) setBag(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [canTrack]);

  // Smart Club Pick — given the **plays-like** distance to the front of the
  // green (or centre if front isn't mapped), find the shortest club whose
  // avg_yards is ≥ target. Then use the club's stdev to estimate the
  // probability that a typical shot will at least reach the front, via the
  // normal-distribution CDF.
  //
  // Goal-Round override (`goalAtRisk=true`): when the player is behind their
  // declared target, the safest play is usually one club up — landing past
  // the front of the green keeps double-bogey risk low. We bump to the next
  // longer club in the bag so the recommendation aims for >50 % probability
  // of carrying the front.
  const clubPick = useMemo(() => {
    if (!bag || bag.length === 0 || !range) return null;
    const flatCentre = range.toCentre;
    const adjCentre = playsLike?.toCentre ?? flatCentre;
    const ratio = flatCentre > 0 ? adjCentre / flatCentre : 1;
    const flatTarget = range.toFront ?? range.toCentre;
    const targetAdj = Math.round(flatTarget * ratio);
    const sorted = [...bag].filter((c) => c.avg_yards > 0).sort((a, b) => a.avg_yards - b.avg_yards);
    if (!sorted.length) return null;
    const stockIdx = sorted.findIndex((c) => c.avg_yards >= targetAdj);
    let chosenIdx;
    if (stockIdx === -1) {
      // Nothing reaches — recommend the longest club regardless of mode.
      chosenIdx = sorted.length - 1;
    } else if (goalAtRisk && stockIdx + 1 < sorted.length) {
      // Goal-Round safety bump — take one more club for the higher carry %.
      chosenIdx = stockIdx + 1;
    } else {
      chosenIdx = stockIdx;
    }
    const pick = sorted[chosenIdx];
    let prob = null;
    if (pick.stdev_yards && pick.stdev_yards > 0) {
      const z = (pick.avg_yards - targetAdj) / pick.stdev_yards;
      prob = Math.round(100 * (0.5 * (1 + erf(z / Math.SQRT2))));
    }
    return {
      club: pick.club, avg: pick.avg_yards, stdev: pick.stdev_yards,
      prob, target: targetAdj, safe: !!(goalAtRisk && chosenIdx !== stockIdx),
    };
  }, [bag, range, playsLike, goalAtRisk]);

  const useMyGPS = () => {
    setLocatingPlayer(true);
    getCurrentPosition({ highAccuracy: true, timeoutMs: 10000 })
      .then((pos) => {
        setPlayerPos({ lat: pos.latitude, lng: pos.longitude });
        toast.success(`GPS locked (±${pos.accuracy?.toFixed(0) || "?"} m)`);
      })
      .catch((err) => toast.error(`GPS failed — ${err.message || err}`))
      .finally(() => setLocatingPlayer(false));
  };

  const trackShotHere = () => {
    if (!canTrack) {
      toast.error("Bula Pro feature — Go Pro to enable shot tracking");
      return;
    }
    if (!target) return;
    onTrackShot?.({ lat: target.lat, lng: target.lng });
  };

  return (
    <div className="relative w-full h-full min-h-[420px]" data-testid="rangefinder-inline">
      {/* Map fills entire pane */}
      <div ref={mapEl} className="absolute inset-0 rounded-xl overflow-hidden" data-testid="rangefinder-inline-map" />

      {/* Header chip — par/yards/SI, no-GPS fallback */}
      <div className="absolute top-2 right-2 z-[1000] flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#0F2537]/85 text-white text-[10px] font-bold backdrop-blur-sm pointer-events-none">
        H{holeNumber} · Par {par} · {totalYards}y
      </div>

      {/* Yards-to-pin readout overlay */}
      {range && (
        <div className="absolute top-2 left-2 z-[1000] bg-[#0F2537]/85 backdrop-blur-sm text-white px-2.5 py-1.5 rounded-xl shadow-lg max-w-[200px]" data-testid="rangefinder-readout">
          <div className="text-[9px] uppercase tracking-widest text-white/60 font-bold">To pin</div>
          <div className="flex items-end gap-1">
            <div className="font-heading font-black text-2xl leading-none tabular-nums" data-testid="rangefinder-yards">
              {playsLike?.toCentre ?? range.toCentre}
            </div>
            <div className="text-[10px] font-bold text-white/70 mb-0.5">
              yd{playsLike && playsLike.delta_m != null && playsLike.delta_m !== 0 ? (playsLike.delta_m > 0 ? " ↑" : " ↓") : ""}
            </div>
          </div>
          {playsLike && playsLike.toCentre !== range.toCentre && (
            <div className="mt-0.5 text-[9px] font-bold text-white/55 tabular-nums" data-testid="rangefinder-flat">
              flat {range.toCentre} yd
            </div>
          )}
          {range.toFront != null && range.toBack != null && range.toFront !== range.toBack && (
            <div className="mt-0.5 text-[9px] font-bold text-white/80 tabular-nums flex gap-1.5">
              <span>F{range.toFront}</span><span className="text-white/30">·</span><span>B{range.toBack}</span>
            </div>
          )}
          {range.carry != null && (
            <div className="mt-0.5 text-[9px] font-bold text-white/55">Carry: {range.carry} yd</div>
          )}
          {playsLike?.wind_label && (
            <div className="mt-1 inline-flex items-center gap-1 text-[9px] font-bold bg-white/10 px-1.5 py-0.5 rounded" data-testid="rangefinder-wind">
              <span aria-hidden="true">🌬</span> {playsLike.wind_label}
            </div>
          )}
          {clubPick && (
            <div className="mt-1.5 px-1.5 py-1 rounded bg-[#E05D3A]/95 text-white text-[10px] font-bold leading-tight" data-testid="rangefinder-club-pick">
              <div className="flex items-center gap-1">
                <Target size={9} weight="fill" />
                <span>Hit your <b className="font-black">{clubPick.club}</b></span>
                {clubPick.safe && (
                  <span className="ml-1 px-1 py-px rounded-full bg-white/20 text-[8px] tracking-wide" data-testid="rangefinder-safe-tag">SAFE</span>
                )}
              </div>
              <div className="text-[9px] font-bold text-white/85 tabular-nums">
                {clubPick.avg}{clubPick.stdev != null ? ` ± ${clubPick.stdev}` : ""} yd
                {clubPick.prob != null && <> · <span className="text-[#FFC72C]">{clubPick.prob}% to reach</span></>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Toolbar — bottom-left */}
      <div className="absolute bottom-2 left-2 z-[1000] flex flex-wrap items-center gap-1.5 max-w-[calc(100%-1rem)]" data-testid="rangefinder-toolbar">
        <button onClick={useMyGPS} disabled={locatingPlayer} data-testid="rf-use-gps-inline"
          className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg bg-white/95 backdrop-blur-sm text-[#0F2537] text-[11px] font-bold shadow-md hover:bg-white disabled:opacity-60">
          <NavigationArrow size={11} weight="fill" />
          {locatingPlayer ? "…" : (playerPos ? "Re-lock" : "GPS")}
        </button>
        <button onClick={trackShotHere} disabled={!canTrack} data-testid="rf-track-shot-inline"
          className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] font-bold shadow-md transition ${canTrack ? "bg-[#E05D3A] text-white hover:brightness-110" : "bg-white/70 text-[#0F2537]/50"}`}
          title={canTrack ? "Save this target as a tracked shot" : "Bula Pro feature"}>
          <Target size={11} weight="fill" /> Track
        </button>
        {onOpenFullFlyover && (
          <button onClick={onOpenFullFlyover} data-testid="rf-open-full-inline"
            className="inline-flex items-center gap-1 px-2 py-1.5 rounded-lg bg-white/95 backdrop-blur-sm text-[#0F2537] text-[11px] font-bold shadow-md hover:bg-white">
            <Crosshair size={11} weight="fill" /> Pro tools
          </button>
        )}
      </div>

      {/* "Score this hole →" CTA — bottom-right. Slides over to scorecard pane. */}
      {onScore && (
        <button onClick={onScore} data-testid="rangefinder-go-score"
          className="absolute bottom-2 right-2 z-[1000] inline-flex items-center gap-1 px-3 py-2 rounded-xl bg-[#0F2537] text-white text-xs font-bold shadow-lg hover:bg-[#0F2537]/90">
          Score <CaretRight size={12} weight="bold" />
        </button>
      )}

      {/* Empty state when the hole has no GPS data yet. */}
      {!teePos && !greenPos && shots.length === 0 && (
        <div className="absolute inset-0 z-[500] flex flex-col items-center justify-center bg-[#0F2537]/30 backdrop-blur-sm text-white text-center px-6">
          <Crosshair size={28} weight="duotone" />
          <p className="text-sm font-bold mt-2">No GPS data for this hole yet</p>
          <p className="text-xs opacity-80 mt-1">Tap "Score" to mark your card without the map.</p>
        </div>
      )}
    </div>
  );
}
